/**
 * LocalCacheProvider.ts — local provider implementing the same StorageProvider
 * interface (SUPABASE FOUNDATION™). Backs the offline layer: always reachable
 * when localStorage exists, zero latency, device-scoped. Documents live under
 * `nomad:docstore:<domain>:<key>` — this does NOT replace the legacy per-service
 * localStorage keys (those remain each Domain Service's synchronous cache);
 * it exists so the manager can fall back and future providers can be swapped
 * without touching business logic.
 */

import type { CloudDomain, ProviderHealth, StorageProvider, StoredDocument } from './StorageProvider';

function storageKey(domain: CloudDomain, key: string): string {
  return `nomad:docstore:${domain}:${key}`;
}

function hasStorage(): boolean {
  try { return typeof localStorage !== 'undefined'; } catch { return false; }
}

export class LocalCacheProvider implements StorageProvider {
  readonly id = 'local';
  readonly remote = false;

  getHealth(): ProviderHealth {
    const ok = hasStorage();
    return {
      id: this.id,
      reachable: ok,
      lastLatencyMs: ok ? 0 : null,
      lastSuccessAt: ok ? new Date().toISOString() : null,
      consecutiveFailures: ok ? 0 : 1,
      detail: ok ? null : 'localStorage unavailable',
    };
  }

  async checkHealth(): Promise<ProviderHealth> {
    return this.getHealth();
  }

  async read<T>(domain: CloudDomain, key: string): Promise<StoredDocument<T> | null> {
    if (!hasStorage()) return null;
    try {
      const raw = localStorage.getItem(storageKey(domain, key));
      return raw ? (JSON.parse(raw) as StoredDocument<T>) : null;
    } catch { return null; }
  }

  async write(domain: CloudDomain, key: string, payload: unknown, updatedAt: string): Promise<void> {
    if (!hasStorage()) return;
    try {
      localStorage.setItem(storageKey(domain, key), JSON.stringify({ payload, updatedAt }));
    } catch { /* quota — cache is best-effort */ }
  }

  async delete(domain: CloudDomain, key: string): Promise<void> {
    if (!hasStorage()) return;
    try { localStorage.removeItem(storageKey(domain, key)); } catch { /* ignore */ }
  }
}
